import React, { useEffect, useState } from 'react';
import Navbar from './../components/Features/Navbar';
import Filter from '../components/Features/Filter';
import { Drawer, IconButton, useMediaQuery, InputAdornment, TextField, Typography, Button, Box, CircularProgress } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import CardPoste from '../components/Features/CardPoste';
import CardPosteSkeleton from '../components/Features/CardPosteSkeleton';

const AdvancedSearch = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [postes, setPostes] = useState([]);
  const [filteredPostes, setFilteredPostes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [search, setSearch] = useState('');
  const isMobile = useMediaQuery('(max-width:992px)');

  useEffect(() => {
    document.title = 'Recherches Postes | Tsahal Connection';

    const fetchAllPostes = async () => {
      try {
        setLoading(true);
        let allPostes = [];
        let currentPage = 1;
        let totalPages = 1;

        // Premier appel pour obtenir le nombre total de pages
        const initialResponse = await fetch(`${process.env.REACT_APP_STRAPI_API_URL}/postes?pagination[page]=1`, {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_STRAPI_API_TOKEN}`,
            'Content-Type': 'application/json',
          },
        });

        if (!initialResponse.ok) {
          throw new Error('Failed to fetch postes');
        }

        const initialData = await initialResponse.json();
        totalPages = initialData.meta.pagination.pageCount;
        
        // Ajouter les postes de la première page
        allPostes = [...allPostes, ...initialData.data];

        // Faire les appels pour les pages restantes
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const remainingPromises = remainingPages.map(page => 
          fetch(`${process.env.REACT_APP_STRAPI_API_URL}/postes?pagination[page]=${page}`, {
            headers: {
              'Authorization': `Bearer ${process.env.REACT_APP_STRAPI_API_TOKEN}`,
              'Content-Type': 'application/json',
            },
          }).then(res => res.json())
        );

        const remainingResults = await Promise.all(remainingPromises);
        remainingResults.forEach(result => {
          allPostes = [...allPostes, ...result.data];
        });
        

        // Transformer les données
        const parsedPostes = allPostes.map(item => ({
          nomPosteFrancais: item.attributes.nomPosteFrancais,
          nomPosteHebreu: item.attributes.nomPosteHebreu,
          category: item.attributes.category,
          needInterview: item.attributes.needInterview || "Non demandée",
          forWomen: item.attributes.forWomen === 'Oui',
          forMen: item.attributes.forMen === 'Oui',
          forVolunteers: item.attributes.forVolunteers === 'Oui',
          chanceToGetJob: item.attributes.chanceToGetJob || "Non precisé",
          dapar: item.attributes.dapar || "Non demandée",
          profile: item.attributes.profile || "Non demandée",
          imc: item.attributes.imc || "Non demandée",
          ivrit: item.attributes.ivrit || "Non demandée",
          caregiving: item.attributes.caregiving || "Non demandée",
          leadership: item.attributes.leadership || "Non demandée",
          teamwork: item.attributes.teamwork || "Non demandée",
          discipline: item.attributes.discipline || "Non demandée",
          maturity: item.attributes.maturity || "Non demandée",
          perseverance: item.attributes.perseverance || "Non demandée",
          InformationProcessing: item.attributes.informationProcessing || "Non demandée",
          management: item.attributes.management || "Non demandée",
          technicalOperation: item.attributes.technicalOperation || "Non demandée",
          attention: item.attributes.attention || "Non demandée",
          perception: item.attributes.perception || "Non demandée",
          instruction: item.attributes.instruction || "Non demandée",
          description: item.attributes.description || "",
          formation: item.attributes.formation || "",
          posteLocation: item.attributes.posteLocation || "",
          typePoste: item.attributes.typePoste || "",
          recruitmentProcess: item.attributes.recruitmentProcess || ""
        }));

        setPostes(parsedPostes);
        setFilteredPostes(parsedPostes);
      } catch (error) {
        console.error('Error fetching postes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllPostes();
  }, []);

  // Add filter effect
  useEffect(() => {
    let filtered = [...postes];

    // Filter by category
    if (filterValues.categories && filterValues.categories.length > 0) {
      filtered = filtered.filter(poste => 
        filterValues.categories.some(category => category.value === poste.category)
      );
    }

    // Filter by gender
    if (filterValues.genres && filterValues.genres.length > 0) {
      filtered = filtered.filter(poste => {
        if (filterValues.genres.includes('Homme') && filterValues.genres.includes('Femme')) {
          return true;
        } else if (filterValues.genres.includes('Homme')) {
          return poste.forMen;
        } else if (filterValues.genres.includes('Femme')) {
          return poste.forWomen;
        }
        return false;
      });
    }

    // Filter by volunteer status
    if (filterValues.volontaires && filterValues.volontaires.length > 0) {
      filtered = filtered.filter(poste => {
        if (filterValues.volontaires.includes('Oui') && filterValues.volontaires.includes('Non')) {
          return true;
        } else if (filterValues.volontaires.includes('Oui')) {
          return poste.forVolunteers;
        } else if (filterValues.volontaires.includes('Non')) {
          return !poste.forVolunteers;
        }
        return false;
      });
    }

    if (filterValues.dapar) {
      filtered = filtered.filter(poste => {
        const posteDapar = parseInt(poste.dapar) || 0;
        return posteDapar <= filterValues.dapar;
      });
    }

    // Filter by profile - show all profiles greater than or equal to selected
    if (filterValues.profile) {
      const selectedProfile = parseInt(filterValues.profile);
      filtered = filtered.filter(poste => {
        const posteProfile = parseInt(poste.profile) || 0;
        return posteProfile <= selectedProfile;
      });
    }

    // Filter by Ivrit level - show all postes with Ivrit level <= selected level
    if (filterValues.ivrit) {
      const selectedIvrit = parseInt(filterValues.ivrit);
      filtered = filtered.filter(poste => {
        const posteIvrit = parseInt(poste.ivrit) || 0;
        return posteIvrit <= selectedIvrit;
      });
    }

    // Filter by Keshev level - show all postes with attention level <= selected level
    if (filterValues.keshev) {
      const selectedKeshev = parseInt(filterValues.keshev);
      filtered = filtered.filter(poste => {
        const posteAttention = parseInt(poste.attention) || 0;
        return posteAttention <= selectedKeshev;
      });
    }

    // Filter by interview requirement
    if (filterValues.entretiens && filterValues.entretiens.length > 0) {
      filtered = filtered.filter(poste => {
        if (filterValues.entretiens.includes('Oui') && filterValues.entretiens.includes('Non')) {
          return true;
        } else if (filterValues.entretiens.includes('Oui')) {
          return poste.needInterview === 'Oui';
        } else if (filterValues.entretiens.includes('Non')) {
          return poste.needInterview !== 'Oui';
        }
        return false;
      });
    }

    // Filter by Yom Hameah requirements
    if (filterValues.requirements) {
      filtered = filtered.filter(poste => {
        return Object.entries(filterValues.requirements).every(([key, userScore]) => {
          if (!userScore || userScore === null) return true; // Pas de filtre pour cette compétence
          
          const posteRequirement = poste[key];
          // Si le poste n'a pas de note pour cette compétence, on l'accepte
          if (!posteRequirement || posteRequirement === '' || posteRequirement === null || posteRequirement === undefined || posteRequirement === "Non demandée") {
            return true;
          }
          
          // Sinon, on vérifie que la note de l'utilisateur est >= à la note requise par le poste
          const posteScore = parseInt(posteRequirement);
          const userScoreInt = parseInt(userScore);
          return !isNaN(posteScore) && !isNaN(userScoreInt) && userScoreInt >= posteScore;
        });
      });
    }

    // Filter by search
    if (search.trim() !== '') {
      const searchLower = search.trim().toLowerCase();
      filtered = filtered.filter(poste =>
        (poste.nomPosteFrancais && poste.nomPosteFrancais.toLowerCase().includes(searchLower)) ||
        (poste.nomPosteHebreu && poste.nomPosteHebreu.toLowerCase().includes(searchLower))
      );
    }

    setFilteredPostes(filtered);
  }, [filterValues, postes, search]);

  // Add styles for hiding scrollbar
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .hide-scrollbar {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleFilterChange = (newFilters) => {
    setFilterValues(newFilters);
  };

  const downloadFilteredPostes = async () => {
    setDownloadLoading(true);
    try {
      // Simuler un petit délai pour le traitement
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const postesText = filteredPostes.map(poste => 
        `${poste.nomPosteFrancais || ''} / ${poste.nomPosteHebreu || ''}`
      ).join('\n');
      
      // Ajouter le BOM UTF-8 pour Android
      const BOM = '\uFEFF';
      const textWithBOM = BOM + postesText;
      
      const blob = new Blob([textWithBOM], { 
        type: 'text/plain;charset=utf-8' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `postes_filtres_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div>
      <Navbar sticky={true} />
      <div className="container-xl recherche-postes" style={{ marginTop: '2rem' }}>
        <div className="row">
        <div style={{borderStyle: 'solid', borderRadius: '20px', backgroundColor: '#EBEAFF', borderColor: '#EBEAFF', marginBottom: '3rem'}}>
            <Typography variant="body1" style={{ fontFamily: 'Montserrat', lineHeight: '1.8', fontSize: '16px', color: '#2f3036', marginBottom: '1rem', fontWeight: '600', padding: '20px' }}>
            ⚠️ <strong>Note importante !</strong> Cet outil a été créé par des volontaires pour vous aider à mieux comprendre les affectations possibles dans Tsahal.
Il ne s'agit pas d'un outil officiel, et des erreurs peuvent subsister malgré nos efforts.

            </Typography> 
        </div>
          {isMobile ? (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <p
                  style={{
                    fontFamily: 'Montserrat',
                    color: '#013D51',
                    fontWeight: '700',
                    fontSize: '1.2rem',
                  }}
                >
                  Recherches de postes
                </p>
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="open filter drawer"
                  onClick={toggleDrawer}
                  sx={{
                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                    border: '1px solid #5A1C7A',
                    borderRadius: '10px',
                    color: '#2D3065',
                  }}
                >
                  <TuneIcon />
                </IconButton>
              </div>
              {/* Search bar mobile */}
              <div style={{ margin: '1rem 0' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Rechercher un poste (français ou hébreu)"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      style: { borderRadius: 15 }
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '15px',
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={downloadFilteredPostes}
                    disabled={downloadLoading}
                    sx={{
                      minWidth: 'auto',
                      height: '40px',
                      borderRadius: '15px',
                      backgroundColor: '#991FD1',
                      '&:hover': {
                        backgroundColor: '#7A1AA6',
                      },
                      '&:disabled': {
                        backgroundColor: '#9E9E9E',
                      },
                    }}
                  >
                    {downloadLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )}
                  </Button>
                </Box>
              </div>
              <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer}
                sx={{ width: '50%' }}
              >
                <div style={{ width: 250, padding: '1rem' }}>
                  <Filter onChange={handleFilterChange} values={filterValues} />
                </div>
              </Drawer>
            </>
          ) : (
            <div className="col-lg-3" style={{ position: 'sticky', top: '2rem', height: 'fit-content' }}>
              <Filter onChange={handleFilterChange} values={filterValues} />
            </div>
          )}
          <div 
            className={`${isMobile ? 'col-12' : 'col-lg-9'} hide-scrollbar`} 
            style={{ 
              marginTop: isMobile ? '2rem' : '0px', 
              maxHeight: 'calc(100vh - 2rem)', 
              overflowY: 'auto'
            }}
          >
            {/* Search bar desktop */}
            {!isMobile && (
              <div style={{ margin: '1rem 0' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Rechercher un poste (français ou hébreu)"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      style: { borderRadius: 15 }
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '15px',
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={downloadFilteredPostes}
                    disabled={downloadLoading}
                    sx={{
                      minWidth: 'auto',
                      height: '40px',
                      borderRadius: '15px',
                      backgroundColor: '#991FD1',
                      '&:hover': {
                        backgroundColor: '#7A1AA6',
                      },
                      '&:disabled': {
                        backgroundColor: '#9E9E9E',
                      },
                    }}
                  >
                    {downloadLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )}
                  </Button>
                </Box>
              </div>
            )}
            {loading ? (
              <>
                {[...Array(5)].map((_, index) => (
                  <CardPosteSkeleton key={index} />
                ))}
              </>
            ) : (
              filteredPostes.map((poste, index) => (
                <div key={index} style={{ marginBottom: '1rem' }}>
                  <CardPoste
                    nomPosteFrancais={poste.nomPosteFrancais}
                    nomPosteHebreu={poste.nomPosteHebreu}
                    category={poste.category}
                    needInterview={poste.needInterview}
                    forWomen={poste.forWomen}
                    forMen={poste.forMen}
                    forVolunteers={poste.forVolunteers}
                    chanceToGetJov={poste.chanceToGetJov}
                    dapar={poste.dapar}
                    profile={poste.profile}
                    imc={poste.imc}
                    ivrit={poste.ivrit}
                    caregiving={poste.caregiving}
                    leadership={poste.leadership}
                    teamwork={poste.teamwork}
                    discipline={poste.discipline}
                    maturity={poste.maturity}
                    perseverance={poste.perseverance}
                    InformationProcessing={poste.InformationProcessing}
                    management={poste.management}
                    technicalOperation={poste.technicalOperation}
                    attention={poste.attention}
                    perception={poste.perception}
                    instruction={poste.instruction}
                    description={poste.description}
                    formation={poste.formation}
                    posteLocation={poste.posteLocation}
                    recruitmentProcess={poste.recruitmentProcess}
                    chanceToGetJob={poste.chanceToGetJob}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearch;
